/**
 * ProfessionalTaxSlabs.js — Superadmin PT Slab Editor
 *
 * Allows superadmin to view and edit Professional Tax slabs for all Indian states.
 * Route: /superadmin/settings → add as a tab inside SuperAdmin Settings
 *
 * Endpoints used:
 *   GET  /payroll/pt-slabs              — list all slabs
 *   POST /payroll/pt-slabs              — add a slab
 *   PUT  /payroll/pt-slabs/:id          — update a slab
 *   DELETE /payroll/pt-slabs/:id        — delete a slab
 *
 * (These routes need to be added to payroll.routes.js — see BACKEND_ROUTES below)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Badge, Button, Modal, Form,
    Row, Col, Spinner, Alert, InputGroup,
} from 'react-bootstrap';
import { Plus, Edit2, Trash2, RefreshCw, Shield, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const INDIAN_STATES = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
    'Delhi','Goa','Gujarat','Haryana','Himachal Pradesh',
    'Jammu and Kashmir','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
    'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha',
    'Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
    'Uttar Pradesh','Uttarakhand','West Bengal',
];

const ZERO_PT_STATES = [
    'Delhi','Uttar Pradesh','Rajasthan','Haryana','Goa',
    'Himachal Pradesh','Uttarakhand','Jammu and Kashmir','Arunachal Pradesh',
];

const fmt = n => n == null ? '—' : '₹' + parseFloat(n).toLocaleString('en-IN');

const EMPTY_FORM = {
    state: '', min_salary: '', max_salary: '', monthly_tax: '', february_tax: '',
};

const ProfessionalTaxSlabs = () => {
    const [slabs,       setSlabs]      = useState([]);
    const [loading,     setLoading]    = useState(true);
    const [showModal,   setShowModal]  = useState(false);
    const [editing,     setEditing]    = useState(null);
    const [form,        setForm]       = useState(EMPTY_FORM);
    const [saving,      setSaving]     = useState(false);
    const [filterState, setFilterState]= useState('');
    const [deleting,    setDeleting]   = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/payroll/pt-slabs');
            setSlabs(res.data.data || []);
        } catch { toast.error('Failed to load PT slabs'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const openAdd = (state = '') => {
        setEditing(null);
        setForm({ ...EMPTY_FORM, state });
        setShowModal(true);
    };

    const openEdit = (slab) => {
        setEditing(slab);
        setForm({
            state:        slab.state,
            min_salary:   String(slab.min_salary ?? ''),
            max_salary:   slab.max_salary != null ? String(slab.max_salary) : '',
            monthly_tax:  String(slab.monthly_tax ?? ''),
            february_tax: slab.february_tax != null ? String(slab.february_tax) : '',
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.state)              { toast.error('State is required'); return; }
        if (form.min_salary === '')   { toast.error('Min salary is required'); return; }
        if (form.monthly_tax === '')  { toast.error('Monthly tax is required'); return; }
        setSaving(true);
        try {
            const payload = {
                state:        form.state,
                min_salary:   parseFloat(form.min_salary) || 0,
                max_salary:   form.max_salary !== '' ? parseFloat(form.max_salary) : null,
                monthly_tax:  parseFloat(form.monthly_tax) || 0,
                february_tax: form.february_tax !== '' ? parseFloat(form.february_tax) : null,
            };
            if (editing) {
                await axiosInstance.put(`/payroll/pt-slabs/${editing.id}`, payload);
                toast.success('PT slab updated');
            } else {
                await axiosInstance.post('/payroll/pt-slabs', payload);
                toast.success('PT slab added');
            }
            setShowModal(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this PT slab? This will affect payroll calculations.')) return;
        setDeleting(id);
        try {
            await axiosInstance.delete(`/payroll/pt-slabs/${id}`);
            toast.success('Slab deleted');
            load();
        } catch { toast.error('Delete failed'); }
        finally { setDeleting(null); }
    };

    // Group slabs by state for display
    const stateGroups = slabs.reduce((acc, s) => {
        if (!acc[s.state]) acc[s.state] = [];
        acc[s.state].push(s);
        return acc;
    }, {});

    const filteredStates = Object.keys(stateGroups)
        .filter(s => !filterState || s === filterState)
        .sort();

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Shield size={20} className="text-primary"/>
                        Professional Tax Slabs
                    </h5>
                    <p className="text-muted small mb-0">
                        Manage state-wise PT slabs. Changes apply from the next payroll run.
                    </p>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Form.Select size="sm" style={{ width:200 }}
                        value={filterState}
                        onChange={e => setFilterState(e.target.value)}>
                        <option value="">All States ({Object.keys(stateGroups).length})</option>
                        {Object.keys(stateGroups).sort().map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </Form.Select>
                    <Button size="sm" variant="outline-secondary" onClick={load} title="Refresh">
                        <RefreshCw size={13}/>
                    </Button>
                    <Button size="sm" variant="primary" onClick={() => openAdd()}>
                        <Plus size={13} className="me-1"/>Add Slab
                    </Button>
                </div>
            </div>

            <Alert variant="info" className="small py-2 mb-4">
                <Info size={13} className="me-1"/>
                <strong>Maharashtra:</strong> February Tax overrides the monthly tax for February only (₹300 instead of ₹200).
                Leave February Tax blank for all other states.
                Setting monthly_tax = ₹0 means the state is PT-exempt (e.g. Delhi, UP).
            </Alert>

            {loading ? (
                <div className="text-center py-4"><Spinner animation="border" variant="primary"/></div>
            ) : filteredStates.length === 0 ? (
                <Alert variant="light" className="border text-center">No PT slabs found.</Alert>
            ) : (
                filteredStates.map(state => {
                    const isZeroPT = ZERO_PT_STATES.includes(state);
                    const stateSlabs = stateGroups[state].sort(
                        (a, b) => (a.min_salary || 0) - (b.min_salary || 0)
                    );
                    return (
                        <Card key={state} className="border-0 shadow-sm mb-3">
                            <Card.Header className="bg-white border-bottom py-2 d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold">{state}</span>
                                    {isZeroPT && (
                                        <Badge bg="success" style={{fontSize:10}}>Zero PT</Badge>
                                    )}
                                    <Badge bg="light" text="dark" style={{fontSize:10}}>
                                        {stateSlabs.length} slab{stateSlabs.length !== 1 ? 's' : ''}
                                    </Badge>
                                </div>
                                <Button size="sm" variant="outline-primary" className="py-0"
                                    onClick={() => openAdd(state)}>
                                    <Plus size={12} className="me-1"/>Add Slab
                                </Button>
                            </Card.Header>
                            <Table hover responsive className="mb-0 align-middle small">
                                <thead className="bg-light">
                                    <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                        <th className="ps-3">Min Salary (₹)</th>
                                        <th>Max Salary (₹)</th>
                                        <th>Monthly Tax (₹)</th>
                                        <th>Feb Tax (₹)</th>
                                        <th className="pe-3 text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stateSlabs.map(slab => (
                                        <tr key={slab.id}>
                                            <td className="ps-3">{fmt(slab.min_salary)}</td>
                                            <td>
                                                {slab.max_salary != null
                                                    ? fmt(slab.max_salary)
                                                    : <span className="text-muted">No limit</span>}
                                            </td>
                                            <td>
                                                <span className={slab.monthly_tax > 0 ? 'fw-bold text-danger' : 'text-muted'}>
                                                    {fmt(slab.monthly_tax)}
                                                </span>
                                            </td>
                                            <td>
                                                {slab.february_tax != null
                                                    ? <span className="fw-bold text-warning">{fmt(slab.february_tax)}</span>
                                                    : <span className="text-muted">—</span>}
                                            </td>
                                            <td className="pe-3 text-end">
                                                <div className="d-flex gap-1 justify-content-end">
                                                    <Button size="sm" variant="outline-secondary"
                                                        className="py-0 px-2"
                                                        onClick={() => openEdit(slab)}>
                                                        <Edit2 size={11}/>
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger"
                                                        className="py-0 px-2"
                                                        disabled={deleting === slab.id}
                                                        onClick={() => handleDelete(slab.id)}>
                                                        {deleting === slab.id
                                                            ? <Spinner size="sm"/>
                                                            : <Trash2 size={11}/>}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    );
                })
            )}

            {/* Add / Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">
                        {editing ? 'Edit PT Slab' : 'Add PT Slab'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">State *</Form.Label>
                            <Form.Select value={form.state}
                                onChange={e => set('state', e.target.value)}
                                disabled={!!editing}>
                                <option value="">Select state…</option>
                                {INDIAN_STATES.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Min Salary (₹) *</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control type="number" min="0" step="1"
                                    placeholder="e.g. 15001"
                                    value={form.min_salary}
                                    onChange={e => set('min_salary', e.target.value)}/>
                            </InputGroup>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">
                                Max Salary (₹)
                                <span className="fw-normal text-muted ms-1">(blank = no limit)</span>
                            </Form.Label>
                            <InputGroup>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control type="number" min="0" step="1"
                                    placeholder="Leave blank for no limit"
                                    value={form.max_salary}
                                    onChange={e => set('max_salary', e.target.value)}/>
                            </InputGroup>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Monthly Tax (₹) *</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control type="number" min="0" step="1"
                                    placeholder="e.g. 200"
                                    value={form.monthly_tax}
                                    onChange={e => set('monthly_tax', e.target.value)}/>
                            </InputGroup>
                            <Form.Text className="text-muted">Set 0 for PT-exempt slabs</Form.Text>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">
                                February Tax (₹)
                                <span className="fw-normal text-muted ms-1">(Maharashtra only)</span>
                            </Form.Label>
                            <InputGroup>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control type="number" min="0" step="1"
                                    placeholder="e.g. 300"
                                    value={form.february_tax}
                                    onChange={e => set('february_tax', e.target.value)}/>
                            </InputGroup>
                            <Form.Text className="text-muted">Leave blank for all other states</Form.Text>
                        </Col>
                        {form.state && form.min_salary !== '' && form.monthly_tax !== '' && (
                            <Col xs={12}>
                                <div className="p-2 rounded bg-light small">
                                    <strong>Preview:</strong> Employees in {form.state} with salary
                                    ₹{parseFloat(form.min_salary || 0).toLocaleString('en-IN')}
                                    {form.max_salary ? ` – ₹${parseFloat(form.max_salary).toLocaleString('en-IN')}` : '+'}
                                    {' '}will pay <strong>₹{form.monthly_tax}/month</strong> PT.
                                </div>
                            </Col>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1"/>Saving…</> : 'Save Slab'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ProfessionalTaxSlabs;

/*
 * ─────────────────────────────────────────────────────────────────────────
 * BACKEND ROUTES to add to payroll.routes.js
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Add these routes to backend/src/modules/payroll/payroll.routes.js:
 *
 * // ── PT Slab management (superadmin only) ─────────────────────────────
 * router.get('/pt-slabs', auth, tenant, checkPermission('platform:admin'), async (req, res) => {
 *     try {
 *         const slabs = await sequelize.query(
 *             `SELECT * FROM professional_tax_slabs WHERE is_active=1 ORDER BY state, min_salary`,
 *             { type: sequelize.QueryTypes.SELECT }
 *         );
 *         return sendResponse(res, 200, 'PT slabs', slabs);
 *     } catch (err) { return sendResponse(res, 500, err.message); }
 * });
 *
 * router.post('/pt-slabs', auth, tenant, checkPermission('platform:admin'), async (req, res) => {
 *     const { state, min_salary, max_salary, monthly_tax, february_tax } = req.body;
 *     try {
 *         await sequelize.query(
 *             `INSERT INTO professional_tax_slabs (state, min_salary, max_salary, monthly_tax, february_tax)
 *              VALUES (?,?,?,?,?)`,
 *             { replacements: [state, min_salary||0, max_salary||null, monthly_tax||0, february_tax||null] }
 *         );
 *         const { invalidatePTCache } = require('./helpers/taxHelper');
 *         invalidatePTCache();
 *         return sendResponse(res, 201, 'PT slab created');
 *     } catch (err) { return sendResponse(res, 500, err.message); }
 * });
 *
 * router.put('/pt-slabs/:id', auth, tenant, checkPermission('platform:admin'), async (req, res) => {
 *     const { state, min_salary, max_salary, monthly_tax, february_tax } = req.body;
 *     try {
 *         await sequelize.query(
 *             `UPDATE professional_tax_slabs
 *              SET state=?, min_salary=?, max_salary=?, monthly_tax=?, february_tax=?
 *              WHERE id=?`,
 *             { replacements: [state, min_salary||0, max_salary||null, monthly_tax||0, february_tax||null, req.params.id] }
 *         );
 *         const { invalidatePTCache } = require('./helpers/taxHelper');
 *         invalidatePTCache();
 *         return sendResponse(res, 200, 'PT slab updated');
 *     } catch (err) { return sendResponse(res, 500, err.message); }
 * });
 *
 * router.delete('/pt-slabs/:id', auth, tenant, checkPermission('platform:admin'), async (req, res) => {
 *     try {
 *         await sequelize.query(
 *             `UPDATE professional_tax_slabs SET is_active=0 WHERE id=?`,
 *             { replacements: [req.params.id] }
 *         );
 *         const { invalidatePTCache } = require('./helpers/taxHelper');
 *         invalidatePTCache();
 *         return sendResponse(res, 200, 'PT slab deleted');
 *     } catch (err) { return sendResponse(res, 500, err.message); }
 * });
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Add ProfessionalTaxSlabs to SuperAdmin Settings page:
 *   import ProfessionalTaxSlabs from '../settings/ProfessionalTaxSlabs';
 *   // Add as a new Tab in your SuperAdmin settings Tabs component
 * ─────────────────────────────────────────────────────────────────────────
 */
